/** Keyboard edits on the draft (pure): nudge, duplicate, select all on a face. */
import type { S } from "../api/client";
import type { Vec2 } from "./geo.ts";
import { faceContext, panelGeometry, toFaceUv } from "./layout.ts";
import { newKey, type ObstacleItem, type PanelItem } from "./model.ts";
import { faceAt, type LiveRoof } from "./roof.ts";
import { fits, footprint, moduleDims, type ModuleSize } from "./snap.ts";

/** Nudge steps: 5 cm, or 50 cm with Shift. */
export const NUDGE_M = 0.05;
export const NUDGE_BIG_M = 0.5;

function centroid(pts: Vec2[]): Vec2 {
  return [pts.reduce((s, p) => s + p[0], 0) / pts.length, pts.reduce((s, p) => s + p[1], 0) / pts.length];
}

/** The selected panels moved by `delta` metres on the plan (each stays on its face; free placement). */
export function nudgePanels(panels: PanelItem[], keys: Set<string>, delta: Vec2, roof: LiveRoof, module: ModuleSize): PanelItem[] {
  return panels.map((p) => {
    if (!keys.has(p.key)) return p;
    const face = roof.faces.find((f) => f.id === p.face_id);
    const plan = panelGeometry(p, roof, module).plan;
    if (!face || !plan.length) return p;
    const c = centroid(plan);
    return { ...p, center: toFaceUv(face, [c[0] + delta[0], c[1] + delta[1]]) };
  });
}

export function nudgeObstacle(obstacles: ObstacleItem[], key: string, delta: Vec2): ObstacleItem[] {
  return obstacles.map((o) => (o.key === key ? { ...o, polygon: o.polygon.map(([x, y]) => [x + delta[0], y + delta[1]] as Vec2) } : o));
}

/** Copies of the selected panels next to them on their face: to the right along the rows, else left, above or
 * below, the first place where (with snapping on) every copy fits. Null when nothing fits. */
export function duplicatePanels(
  panels: PanelItem[],
  keys: Set<string>,
  roof: LiveRoof,
  obstacles: ObstacleItem[],
  setbacks: S["SetbacksOutput"] | null | undefined,
  module: ModuleSize,
  gap: number,
  sticky: boolean,
): { panels: PanelItem[]; copies: string[] } | null {
  const chosen = panels.filter((p) => keys.has(p.key));
  if (!chosen.length) return null;
  const extents = chosen.map((p) => moduleDims(p.orientation, module));
  const us = chosen.map((p, i) => [p.center[0] - extents[i]![0] / 2, p.center[0] + extents[i]![0] / 2]);
  const vs = chosen.map((p, i) => [p.center[1] - extents[i]![1] / 2, p.center[1] + extents[i]![1] / 2]);
  const width = Math.max(...us.map((u) => u[1]!)) - Math.min(...us.map((u) => u[0]!)) + gap;
  const height = Math.max(...vs.map((v) => v[1]!)) - Math.min(...vs.map((v) => v[0]!)) + gap;
  const offsets: Vec2[] = [[width, 0], [-width, 0], [0, height], [0, -height]];
  for (const [du, dv] of offsets) {
    const copies = chosen.map((p) => ({ ...p, key: newKey("p"), id: null, center: [p.center[0] + du, p.center[1] + dv] as Vec2 }));
    const all = [...panels, ...copies];
    const ok =
      !sticky ||
      copies.every((c) => {
        const face = roof.faces.find((f) => f.id === c.face_id);
        if (!face) return false;
        const ctx = faceContext(face, roof, all, obstacles, setbacks, module, new Set([c.key]));
        return fits(footprint(c.center, c.orientation, c.rotation, module), ctx);
      });
    if (ok) return { panels: all, copies: copies.map((c) => c.key) };
  }
  return null;
}

/** The face to select all on: the selection's, else the face under the pointer. */
export function faceForSelectAll(panels: PanelItem[], selected: string[], faceId: string | null, pointer: Vec2 | null, roof: LiveRoof): string | null {
  const first = panels.find((p) => selected.includes(p.key));
  if (first) return first.face_id;
  if (faceId) return faceId;
  return pointer ? faceAt(roof, pointer)?.id ?? null : null;
}
